import {getCanvasContents, getCanvasSelection, getCanvasEditingWidgets, getCanvasSavedScrollPositions, getCanvasSelectedLayerIds, widgetMatchesSelectedLayers, useAppSelector} from '@dv5/domain'
import {useMemo} from "react"
import {useCanvasSettings} from "./useCanvasSettings"
import {CanvasPosition} from '../../model/CanvasPosition'

export interface CanvasContentState {
    canvasContent: { widgets: Array<any>, widgetLinks: Array<any> }
    canvasSelection: Array<string>
    editingWidgetId: string | undefined
    savedScrollPosition: CanvasPosition | undefined
}

export const useCanvasContent = (canvasId: string): CanvasContentState => {
    const {readOnly} = useCanvasSettings(canvasId)
    const canvasContents = useAppSelector(getCanvasContents)
    const canvasSelections = useAppSelector(getCanvasSelection)
    const canvasEditingWidgets = useAppSelector(getCanvasEditingWidgets)
    const savedScrollPositions = useAppSelector(getCanvasSavedScrollPositions)
    const selectedLayerIdsMap = useAppSelector(getCanvasSelectedLayerIds)

    const canvasContent = useMemo(() => {
        const content = canvasContents[canvasId] || {widgets: [], widgetLinks: []}
        const selectedLayerIds = selectedLayerIdsMap[canvasId] ?? []
        const visibleWidgets = content.widgets.filter(widget => widgetMatchesSelectedLayers(widget, selectedLayerIds))
        const widgets = readOnly ? visibleWidgets.map(widget => ({...widget, locked: true})) : visibleWidgets
        return {...content, widgets}
    }, [canvasId, canvasContents, selectedLayerIdsMap, readOnly])

    const canvasSelection = useMemo(() => {
        return canvasSelections[canvasId] || []
    }, [canvasId, canvasSelections])

    const editingWidgetId = useMemo(() => {
        return readOnly ? undefined : canvasEditingWidgets[canvasId]
    }, [canvasId, canvasEditingWidgets, readOnly])

    const savedScrollPosition = useMemo(() => {
        return savedScrollPositions[canvasId]
    }, [canvasId, savedScrollPositions])

    return {canvasContent, canvasSelection, editingWidgetId, savedScrollPosition}
}
